// =====================================================================
// OrderRdv.jsx — Commandes prêtes v3 B (16-10-2026) — page PUBLIQUE /order/<token>/rdv
// =====================================================================
// Bouton vert du mail « ready for collection » : le transporteur du client (ou le client) choisit le
// JOUR (30 prochains jours ouvrés) + matin / après-midi — décision Silviu 16/10 : pas d'heure,
// rendez-vous CONFIRMÉ AUTOMATIQUEMENT, n'importe qui ayant le lien peut réserver ; un nouveau choix
// remplace l'ancien. Bilingue (anglais puis français), plein écran comme OrderPhotos.jsx. Le jeton du
// lien EST l'accès ; toute erreur de jeton → « This link has expired or is invalid. »
// Jamais d'id interne ni les coordonnées du demandeur précédent (le serveur n'en envoie pas).
// =====================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicReadyRdvDocs, errMsg } from '../services/api.js';
import { DEMI_JOURNEES, jourCourt, texteRdv, texteRdvPris, verifRdv, texteManque, corpsRdv } from './commande-prete/rdvDocs.js';
import { useMetaPublique, Entete, Invalide, Chargement, ErreurChargement, Pied, S } from './commande-prete/PagePublique.jsx';

const VIDE = { jour: '', demiJournee: '', societe: '', nom: '', email: '', tel: '', plaque: '' };

export default function OrderRdv() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [etat, setEtat] = useState('loading');   // loading | ok | invalid
  const [f, setF] = useState(VIDE);
  const [envoi, setEnvoi] = useState(false);
  const [msg, setMsg] = useState(null);          // { ok, texte }
  useMetaPublique(data ? `${data.orderRef} — pick-up` : null);

  const lu = useRef(false);   // page déjà affichée : une relecture ratée (réseau) n'efface pas l'écran
  // silencieux : relecture après une réponse du serveur — son échec n'efface jamais le message affiché.
  const charger = useCallback(({ silencieux = false } = {}) => publicReadyRdvDocs.rdv(token)
    .then((d) => { lu.current = true; setData(d); setEtat('ok'); })
    .catch((e) => {
      if (e?.response?.status === 404) setEtat('invalid');
      else if (!lu.current) setEtat('erreur');   // (pas « lien invalide » : réseau / serveur — on peut réessayer)
      else if (!silencieux) setMsg({ ok: false, texte: errMsg(e) });
    }), [token]);
  useEffect(() => { charger(); }, [charger]);

  if (etat === 'loading') return <Chargement />;
  if (etat === 'invalid') return <Invalide />;
  if (etat === 'erreur') return <ErreurChargement onReessayer={() => { setEtat('loading'); charger(); }} />;

  const maj = (k, v) => { setF((x) => ({ ...x, [k]: v })); setMsg(null); };
  // (plus aucune modification possible, ou aucun jour avant la fin du lien : formulaire fermé, « contactez-nous »
  // — jamais un choix proposé puis refusé)
  const bloque = data.enlevee || data.modificationsRestantes <= 0 || !data.jours.length;

  async function confirmer() {
    const pb = verifRdv(f, data.jours);
    if (pb.length) { setMsg({ ok: false, texte: texteManque(pb) }); return; }
    setEnvoi(true);
    try {
      const r = await publicReadyRdvDocs.prendreRdv(token, corpsRdv(f));
      // (confirmationA : seulement l'adresse qui reçoit VRAIMENT la confirmation — sinon le message dit
      // qu'aucun mail n'y part : jamais promis à tort)
      setMsg({ ok: true, ...texteRdvPris(r) });
      setF(VIDE);
      await charger({ silencieux: true });
    } catch (e) {
      if (e?.response?.status === 404) { setEtat('invalid'); return; }
      setMsg({ ok: false, texte: errMsg(e) });
      // jour plus proposé (400), commande enlevée (409), trop de changements (429) : l'écran se remet à jour
      if ([400, 409, 429].includes(e?.response?.status)) charger({ silencieux: true });
    } finally { setEnvoi(false); }
  }

  return (
    <div style={S.wrap}>
      <Entete titreEn="Book a pick-up time" titreFr="Prendre rendez-vous pour l'enlèvement" data={data} />

      {data.actuel && (
        <div style={S.info} id="rdvActuel">
          <b>Current booking</b>: {texteRdv(data.actuel, 'en')} — {data.actuel.societe}<br />
          <span style={S.fr}><b>Rendez-vous actuel</b> : {texteRdv(data.actuel, 'fr')} — {data.actuel.societe}</span><br />
          {!bloque && <span style={S.petit}>A new choice replaces it. / Un nouveau choix le remplace.</span>}
        </div>
      )}
      {data.enlevee && <div role="alert" style={S.alerte}>This order has already been collected. / Cette commande a déjà été enlevée.</div>}
      {!data.enlevee && data.modificationsRestantes <= 0 && (
        <div role="alert" style={S.alerte}>Too many changes for this order: please contact us. / Trop de modifications pour cette commande : contactez-nous.</div>
      )}
      {!data.enlevee && data.modificationsRestantes > 0 && !data.jours.length && (
        <div role="alert" style={S.alerte}>This link expires before the next working day: please contact us. / Ce lien expire avant le prochain jour ouvré : contactez-nous.</div>
      )}

      {!bloque && (
        <fieldset disabled={envoi} style={S.form}>
          <div>
            <div style={S.label} id="lblJour">Day / Jour *</div>
            <div style={S.jours} role="group" aria-labelledby="lblJour">
              {data.jours.map((j) => (
                <button key={j} type="button" aria-pressed={f.jour === j} onClick={() => maj('jour', j)} style={f.jour === j ? { ...S.jour, ...S.choisi } : S.jour}>
                  {jourCourt(j, 'en')}<br /><span style={{ fontSize: 11, fontWeight: 500, opacity: 0.85 }}>{jourCourt(j, 'fr').split(' ')[0]}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div style={S.label} id="lblDemi">Morning or afternoon / Matin ou après-midi *</div>
            {!f.jour ? <p style={S.petit}>Choose a day first. / Choisissez d'abord un jour.</p> : (
              <div style={S.demis} role="group" aria-labelledby="lblDemi">
                {Object.entries(DEMI_JOURNEES).map(([code, d]) => (
                  <button key={code} type="button" aria-pressed={f.demiJournee === code} onClick={() => maj('demiJournee', code)}
                    style={f.demiJournee === code ? { ...S.demi, ...S.choisi } : S.demi}>
                    {d.en}<br /><span style={{ fontSize: 12, fontWeight: 500 }}>{d.fr}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div style={S.ligne}>
            <Champ id="r-soc" label="Company / Société *" value={f.societe} onChange={(v) => maj('societe', v)} placeholder="e.g. Transports Dupont" max={100} autoComplete="organization" />
            <Champ id="r-nom" label="Contact name / Nom *" value={f.nom} onChange={(v) => maj('nom', v)} max={100} autoComplete="name" />
          </div>
          <div style={S.ligne}>
            <Champ id="r-mail" type="email" label="Contact email / E-mail de contact *" value={f.email} onChange={(v) => maj('email', v)} max={254} autoComplete="email" />
            <Champ id="r-tel" type="tel" label="Phone / Téléphone" value={f.tel} onChange={(v) => maj('tel', v)} max={30} autoComplete="tel" />
            <Champ id="r-plaque" label="Truck plate / Plaque" value={f.plaque} onChange={(v) => maj('plaque', v)} max={20} />
          </div>
          <div>
            <button type="button" id="r-ok" onClick={confirmer} style={S.bouton}>
              {envoi ? '⏳ …' : 'Confirm the booking / Confirmer le rendez-vous'}
            </button>
          </div>
        </fieldset>
      )}
      {msg && (
        <div role={msg.ok ? 'status' : 'alert'} style={msg.ok ? S.ok : S.erreur} id="r-msg">
          {msg.texte}{msg.texteFr && <><br /><span>{msg.texteFr}</span></>}
        </div>
      )}
      <Pied />
    </div>
  );
}

function Champ({ id, label, value, onChange, type = 'text', placeholder, max, autoComplete }) {
  return (
    <div style={S.champ}>
      <label htmlFor={id} style={S.label}>{label}</label>
      <input id={id} type={type} value={value} placeholder={placeholder} maxLength={max} autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)} style={S.input} />
    </div>
  );
}
